import { EventCatalogFromKeys, EventType } from "../types";

export const AuthEventKeys = {
  AUTH_ACCEPT_INVITE_FAILURE: "auth-accept-invite-failure",
  AUTH_ACCEPT_INVITE_SUCCESS: "auth-accept-invite-success",
  AUTH_EMAIL_VERIFICATION_BANNER_RESEND_ATTEMPT:
    "auth-email-verification-banner-resend-attempt",
  AUTH_EMAIL_VERIFICATION_BANNER_RESEND_FAILURE:
    "auth-email-verification-banner-resend-failure",
  AUTH_EMAIL_VERIFICATION_BANNER_RESEND_SUCCESS:
    "auth-email-verification-banner-resend-success",
  AUTH_ERROR: "auth-error",
  AUTH_FORGOT_PASSWORD_FORM_SUBMIT_ATTEMPT:
    "auth-forgot-password-form-submit-attempt",
  AUTH_FORGOT_PASSWORD_FORM_SUBMIT_FAILURE:
    "auth-forgot-password-form-submit-failure",
  AUTH_FORGOT_PASSWORD_FORM_SUBMIT_SUCCESS:
    "auth-forgot-password-form-submit-success",
  AUTH_FORGOT_PASSWORD_PAGE_VIEW: "auth-forgot-password-page-view",
  AUTH_IDENTIFY_USER: "auth-identify-user",
  AUTH_INVITE_INVALID_LINK: "auth-invite-invalid-link",
  AUTH_INVITE_NO_INVITATION: "auth-invite-no-invitation",
  AUTH_LOGIN_FORM_SUBMIT_ATTEMPT: "auth-login-form-submit-attempt",
  AUTH_LOGIN_FORM_SUBMIT_FAILURE: "auth-login-form-submit-failure",
  AUTH_LOGIN_FORM_SUBMIT_SUCCESS: "auth-login-form-submit-success",
  AUTH_LOGIN_PAGE_VIEW: "auth-login-page-view",
  AUTH_NAV_SWITCH_TO_REGISTER: "auth-nav-switch-to-register",
  AUTH_REGISTER_FORM_SUBMIT_ATTEMPT: "auth-register-form-submit-attempt",
  AUTH_REGISTER_FORM_SUBMIT_FAILURE: "auth-register-form-submit-failure",
  AUTH_REGISTER_FORM_SUBMIT_SUCCESS: "auth-register-form-submit-success",
  AUTH_REGISTER_PAGE_VIEW: "auth-register-page-view",
  AUTH_RESET_PASSWORD_FORM_SUBMIT_ATTEMPT:
    "auth-reset-password-form-submit-attempt",
  AUTH_RESET_PASSWORD_FORM_SUBMIT_FAILURE:
    "auth-reset-password-form-submit-failure",
  AUTH_RESET_PASSWORD_FORM_SUBMIT_SUCCESS:
    "auth-reset-password-form-submit-success",
  AUTH_VERIFY_EMAIL_PAGE_VIEW: "auth-verify-email-page-view",
  USER_LOGGED_OUT: "user-logged-out",
} as const;

export const AuthEventCatalog: EventCatalogFromKeys<typeof AuthEventKeys> = {
  [AuthEventKeys.AUTH_EMAIL_VERIFICATION_BANNER_RESEND_ATTEMPT]: {
    description:
      "User attempted to resend verification email from the dashboard banner",
    eventType: EventType.INTERACTION,
    id: AuthEventKeys.AUTH_EMAIL_VERIFICATION_BANNER_RESEND_ATTEMPT,
  },
  [AuthEventKeys.AUTH_EMAIL_VERIFICATION_BANNER_RESEND_FAILURE]: {
    description:
      "User failed to resend verification email from the dashboard banner",
    eventType: EventType.ERROR,
    id: AuthEventKeys.AUTH_EMAIL_VERIFICATION_BANNER_RESEND_FAILURE,
  },
  [AuthEventKeys.AUTH_EMAIL_VERIFICATION_BANNER_RESEND_SUCCESS]: {
    description:
      "User successfully resent verification email from the dashboard banner",
    eventType: EventType.INTERACTION,
    id: AuthEventKeys.AUTH_EMAIL_VERIFICATION_BANNER_RESEND_SUCCESS,
  },
  [AuthEventKeys.AUTH_ERROR]: {
    description: "User encountered an authentication error",
    eventType: EventType.ERROR,
    id: AuthEventKeys.AUTH_ERROR,
  },
  [AuthEventKeys.AUTH_FORGOT_PASSWORD_FORM_SUBMIT_ATTEMPT]: {
    description: "Public user attempted to submit the forgot password form",
    eventType: EventType.FORM_SUBMISSION,
    id: AuthEventKeys.AUTH_FORGOT_PASSWORD_FORM_SUBMIT_ATTEMPT,
  },
  [AuthEventKeys.AUTH_FORGOT_PASSWORD_FORM_SUBMIT_FAILURE]: {
    description: "Public user failed to submit the forgot password form",
    eventType: EventType.ERROR,
    id: AuthEventKeys.AUTH_FORGOT_PASSWORD_FORM_SUBMIT_FAILURE,
  },
  [AuthEventKeys.AUTH_FORGOT_PASSWORD_FORM_SUBMIT_SUCCESS]: {
    description: "Public user successfully submitted the forgot password form",
    eventType: EventType.FORM_SUBMISSION,
    id: AuthEventKeys.AUTH_FORGOT_PASSWORD_FORM_SUBMIT_SUCCESS,
  },
  [AuthEventKeys.AUTH_FORGOT_PASSWORD_PAGE_VIEW]: {
    description: "User viewed the forgot password page",
    eventType: EventType.PAGE_VIEW,
    id: AuthEventKeys.AUTH_FORGOT_PASSWORD_PAGE_VIEW,
  },
  [AuthEventKeys.AUTH_LOGIN_FORM_SUBMIT_ATTEMPT]: {
    description: "Public user attempted to submit the login form",
    eventType: EventType.FORM_SUBMISSION,
    id: AuthEventKeys.AUTH_LOGIN_FORM_SUBMIT_ATTEMPT,
  },
  [AuthEventKeys.AUTH_LOGIN_FORM_SUBMIT_FAILURE]: {
    description: "Public user failed to submit the login form",
    eventType: EventType.ERROR,
    id: AuthEventKeys.AUTH_LOGIN_FORM_SUBMIT_FAILURE,
  },
  [AuthEventKeys.AUTH_LOGIN_FORM_SUBMIT_SUCCESS]: {
    description: "Public user successfully submitted the login form",
    eventType: EventType.FORM_SUBMISSION,
    id: AuthEventKeys.AUTH_LOGIN_FORM_SUBMIT_SUCCESS,
  },
  [AuthEventKeys.AUTH_LOGIN_PAGE_VIEW]: {
    description: "User viewed the login page",
    eventType: EventType.PAGE_VIEW,
    id: AuthEventKeys.AUTH_LOGIN_PAGE_VIEW,
  },

  [AuthEventKeys.AUTH_NAV_SWITCH_TO_REGISTER]: {
    description: "User switched from login to register view",
    eventType: EventType.INTERACTION,
    id: AuthEventKeys.AUTH_NAV_SWITCH_TO_REGISTER,
  },
  [AuthEventKeys.AUTH_REGISTER_FORM_SUBMIT_ATTEMPT]: {
    description: "Public user attempted to submit the register form",
    eventType: EventType.FORM_SUBMISSION,
    id: AuthEventKeys.AUTH_REGISTER_FORM_SUBMIT_ATTEMPT,
  },
  [AuthEventKeys.AUTH_REGISTER_FORM_SUBMIT_FAILURE]: {
    description: "Public user failed to submit the register form",
    eventType: EventType.ERROR,
    id: AuthEventKeys.AUTH_REGISTER_FORM_SUBMIT_FAILURE,
  },
  [AuthEventKeys.AUTH_REGISTER_FORM_SUBMIT_SUCCESS]: {
    description: "Public user successfully submitted the register form",
    eventType: EventType.FORM_SUBMISSION,
    id: AuthEventKeys.AUTH_REGISTER_FORM_SUBMIT_SUCCESS,
  },
  [AuthEventKeys.AUTH_REGISTER_PAGE_VIEW]: {
    description: "User viewed the register page",
    eventType: EventType.PAGE_VIEW,
    id: AuthEventKeys.AUTH_REGISTER_PAGE_VIEW,
  },
  [AuthEventKeys.AUTH_RESET_PASSWORD_FORM_SUBMIT_ATTEMPT]: {
    description: "Public user attempted to submit the reset password form",
    eventType: EventType.FORM_SUBMISSION,
    id: AuthEventKeys.AUTH_RESET_PASSWORD_FORM_SUBMIT_ATTEMPT,
  },
  [AuthEventKeys.AUTH_RESET_PASSWORD_FORM_SUBMIT_FAILURE]: {
    description: "Public user failed to submit the reset password form",
    eventType: EventType.ERROR,
    id: AuthEventKeys.AUTH_RESET_PASSWORD_FORM_SUBMIT_FAILURE,
  },
  [AuthEventKeys.AUTH_RESET_PASSWORD_FORM_SUBMIT_SUCCESS]: {
    description: "Public user successfully submitted the reset password form",
    eventType: EventType.FORM_SUBMISSION,
    id: AuthEventKeys.AUTH_RESET_PASSWORD_FORM_SUBMIT_SUCCESS,
  },
  [AuthEventKeys.AUTH_VERIFY_EMAIL_PAGE_VIEW]: {
    description: "User viewed the verify email page",
    eventType: EventType.PAGE_VIEW,
    id: AuthEventKeys.AUTH_VERIFY_EMAIL_PAGE_VIEW,
  },
  [AuthEventKeys.AUTH_ACCEPT_INVITE_FAILURE]: {
    description: "User failed to accept an invite",
    eventType: EventType.ERROR,
    id: AuthEventKeys.AUTH_ACCEPT_INVITE_FAILURE,
  },
  [AuthEventKeys.AUTH_ACCEPT_INVITE_SUCCESS]: {
    description: "User successfully accepted an invite",
    eventType: EventType.FORM_SUBMISSION,
    id: AuthEventKeys.AUTH_ACCEPT_INVITE_SUCCESS,
  },
  [AuthEventKeys.AUTH_INVITE_INVALID_LINK]: {
    description: "User attempted to use an invalid invite link",
    eventType: EventType.ERROR,
    id: AuthEventKeys.AUTH_INVITE_INVALID_LINK,
  },
  [AuthEventKeys.AUTH_INVITE_NO_INVITATION]: {
    description: "User attempted to access invite without invitation",
    eventType: EventType.ERROR,
    id: AuthEventKeys.AUTH_INVITE_NO_INVITATION,
  },
  [AuthEventKeys.AUTH_IDENTIFY_USER]: {
    description: "User identified",
    eventType: EventType.IDENTIFY,
    id: AuthEventKeys.AUTH_IDENTIFY_USER,
  },
  [AuthEventKeys.USER_LOGGED_OUT]: {
    description: "User logged out",
    eventType: EventType.RESET,
    id: AuthEventKeys.USER_LOGGED_OUT,
  },
};
